import { BellRing, X } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { PerformanceSetting } from 'shared/enums/performance-setting/performance-setting.enum';
import { notificationsApi } from 'src/api/notifications/notifications';
import { performanceSettingsApi } from 'src/api/performance-settings/performance-settings';
import { ToastType } from 'src/components/Toast/toast-type.enum';
import { useUserAccountDetailsContext } from 'src/contexts/UserAccountDetailsContext/UserAccountDetailsContext';
import { useUserSettingsContext } from 'src/contexts/UserSettingsContext/UserSettingsContext';
import { useTranslationByKey } from 'src/hooks/use-translation-by-key';
import { useUserCurrency } from 'src/hooks/use-user-currency';
import {
  canUseBrowserNotifications,
  getExistingBrowserNotificationSubscription,
  NOTIFICATION_PROMPT_CONTEXT_EVENT,
  NOTIFICATION_PROMPT_CONTEXT_STORAGE_KEY,
  NotificationPromptContext,
  requestBrowserNotificationSubscription,
} from 'src/utils/notifications/browser-notifications';
import { showToast } from 'src/utils/toast-utils';

const DEFAULT_NOTIFICATION_OPT_IN_POINTS = 10;

const NotificationOptInBar: React.FC = () => {
  const t = useTranslationByKey('NOTIFICATIONS');
  const { formatPoints } = useUserCurrency();
  const { userSettingsLoaded, refreshUserSettings } = useUserSettingsContext();
  const { refreshAccountDetails } = useUserAccountDetailsContext();
  const [pointsReward, setPointsReward] = useState(DEFAULT_NOTIFICATION_OPT_IN_POINTS);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLocalSubscription, setHasLocalSubscription] = useState<boolean | null>(null);
  const [localPromptDismissed, setLocalPromptDismissed] = useState(false);
  const [promptContext, setPromptContext] = useState<NotificationPromptContext | null>(null);

  useEffect(() => {
    if (!userSettingsLoaded || !canUseBrowserNotifications()) {
      return;
    }

    setLocalPromptDismissed(localStorage.getItem('rewardbloom.browser-notifications-prompt-dismissed') === 'true');
    setPromptContext(localStorage.getItem(NOTIFICATION_PROMPT_CONTEXT_STORAGE_KEY) as NotificationPromptContext | null);
    void getExistingBrowserNotificationSubscription()
      .then((subscription) => setHasLocalSubscription(subscription !== null))
      .catch(() => setHasLocalSubscription(false));
  }, [userSettingsLoaded]);

  useEffect(() => {
    const handleContext = (event: Event) => {
      setPromptContext((event as CustomEvent<NotificationPromptContext>).detail);
    };
    window.addEventListener(NOTIFICATION_PROMPT_CONTEXT_EVENT, handleContext);
    return () => window.removeEventListener(NOTIFICATION_PROMPT_CONTEXT_EVENT, handleContext);
  }, []);

  useEffect(() => {
    const loadPointsReward = async () => {
      try {
        const performanceSettings = await performanceSettingsApi.getPerformanceSettings();
        const configuredPoints = Number(performanceSettings[PerformanceSetting.BrowserNotificationsOptInPoints]);

        if (Number.isFinite(configuredPoints) && configuredPoints >= 0) {
          setPointsReward(Math.trunc(configuredPoints));
        }
      } catch (error) {
        console.error('Failed to load browser notification opt-in points:', error);
      }
    };

    void loadPointsReward();
  }, []);

  const shouldShow = useMemo(() => {
    return (
      userSettingsLoaded &&
      canUseBrowserNotifications() &&
      hasLocalSubscription === false &&
      !!promptContext &&
      !localPromptDismissed
    );
  }, [userSettingsLoaded, hasLocalSubscription, promptContext, localPromptDismissed]);

  const handleGetNotified = async () => {
    setIsLoading(true);

    try {
      const vapidPublicKey = await notificationsApi.getVapidPublicKey();
      const { permission, subscription } = await requestBrowserNotificationSubscription(vapidPublicKey);

      if (permission !== 'granted') {
        showToast(t('PERMISSION_DENIED'), ToastType.Error);
        return;
      }

      if (!subscription) {
        showToast(t('ENABLE_ERROR'), ToastType.Error);
        return;
      }

      const response = await notificationsApi.updateBrowserNotifications(true, subscription);
      setHasLocalSubscription(true);
      localStorage.setItem('rewardbloom.browser-notifications-prompt-dismissed', 'true');
      localStorage.removeItem(NOTIFICATION_PROMPT_CONTEXT_STORAGE_KEY);
      setPromptContext(null);

      await Promise.all([refreshUserSettings(), refreshAccountDetails()]);

      if (response.pointsAwarded > 0) {
        showToast(t('ENABLED_WITH_POINTS', { points: formatPoints(response.pointsAwarded) }), ToastType.Success);
        return;
      }

      showToast(t('ENABLED'), ToastType.Success);
    } catch (error) {
      console.error('Failed to enable browser notifications:', error);
      showToast(t('ENABLE_ERROR'), ToastType.Error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleNotInterested = async () => {
    setIsLoading(true);

    try {
      await notificationsApi.dismissBrowserNotificationPrompt();
      setLocalPromptDismissed(true);
      localStorage.setItem('rewardbloom.browser-notifications-prompt-dismissed', 'true');
      localStorage.removeItem(NOTIFICATION_PROMPT_CONTEXT_STORAGE_KEY);
      setPromptContext(null);
      await refreshUserSettings();
    } catch (error) {
      console.error('Failed to dismiss browser notification prompt:', error);
      showToast(t('DISMISS_ERROR'), ToastType.Error);
    } finally {
      setIsLoading(false);
    }
  };

  if (!shouldShow) {
    return null;
  }

  return (
    <div className="fixed bottom-24 left-4 right-4 z-[120] md:bottom-6 md:left-[20rem] md:right-6">
      <div className="mx-auto flex max-w-4xl flex-col gap-3 rounded-lg border border-emerald-200 bg-white px-4 py-3 shadow-xl md:flex-row md:items-center md:justify-between">
        <div className="flex min-w-0 items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-emerald-50 text-emerald-700">
            <BellRing className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-bold text-gray-900 md:text-base">
              {t(
                promptContext === 'game'
                  ? 'CONTEXT_GAME_TITLE'
                  : promptContext === 'deal'
                    ? 'CONTEXT_DEAL_TITLE'
                    : promptContext === 'pwa-install'
                      ? 'CONTEXT_PWA_TITLE'
                      : promptContext === 'scheduled-session'
                        ? 'CONTEXT_SESSION_TITLE'
                        : 'CONTEXT_SAVED_TITLE',
                { points: formatPoints(pointsReward) }
              )}
            </p>
            <p className="mt-0.5 text-xs font-medium text-gray-600 md:text-sm">
              {t(promptContext === 'pwa-install' ? 'CONTEXT_PWA_SUBTITLE' : 'CONTEXT_SUBTITLE', {
                points: formatPoints(pointsReward),
              })}
            </p>
          </div>
        </div>

        <div className="flex shrink-0 gap-2">
          <button
            type="button"
            onClick={() => void handleGetNotified()}
            disabled={isLoading}
            className="inline-flex min-h-10 flex-1 items-center justify-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-bold text-white transition-colors hover:bg-emerald-700 disabled:opacity-60 md:flex-none"
          >
            <BellRing className="h-4 w-4" />
            {isLoading ? t('WORKING') : t(promptContext === 'pwa-install' ? 'ALLOW_NOTIFICATIONS' : 'GET_NOTIFIED')}
          </button>
          <button
            type="button"
            onClick={() => void handleNotInterested()}
            disabled={isLoading}
            className="inline-flex min-h-10 flex-1 items-center justify-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm font-bold text-red-700 transition-colors hover:bg-red-100 disabled:opacity-60 md:flex-none"
          >
            <X className="h-4 w-4" />
            {t('NOT_INTERESTED')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NotificationOptInBar;
